/**
 * Audit Logging Types
 * Comprehensive activity tracking for admin panel actions
 */

export enum AuditAction {
  // Authentication
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  LOGIN_FAILED = 'LOGIN_FAILED',
  PASSWORD_RESET = 'PASSWORD_RESET',
  
  // Product Operations
  PRODUCT_CREATE = 'PRODUCT_CREATE',
  PRODUCT_UPDATE = 'PRODUCT_UPDATE',
  PRODUCT_DELETE = 'PRODUCT_DELETE',
  PRODUCT_TOGGLE_AVAILABILITY = 'PRODUCT_TOGGLE_AVAILABILITY',
  PRODUCT_PRICE_UPDATE = 'PRODUCT_PRICE_UPDATE',
  PRODUCT_STOCK_UPDATE = 'PRODUCT_STOCK_UPDATE',
  
  // Category Operations
  CATEGORY_CREATE = 'CATEGORY_CREATE',
  CATEGORY_UPDATE = 'CATEGORY_UPDATE',
  CATEGORY_DELETE = 'CATEGORY_DELETE',
  
  // Order Operations
  ORDER_STATUS_UPDATE = 'ORDER_STATUS_UPDATE',
  ORDER_ASSIGN_DELIVERY = 'ORDER_ASSIGN_DELIVERY',
  ORDER_CANCEL = 'ORDER_CANCEL',
  ORDER_REFUND = 'ORDER_REFUND',
  
  // Delivery Agent Operations
  DELIVERY_AGENT_CREATE = 'DELIVERY_AGENT_CREATE',
  DELIVERY_AGENT_UPDATE = 'DELIVERY_AGENT_UPDATE',
  DELIVERY_AGENT_DELETE = 'DELIVERY_AGENT_DELETE',
  DELIVERY_AGENT_VERIFY = 'DELIVERY_AGENT_VERIFY',
  DELIVERY_AGENT_STATUS_CHANGE = 'DELIVERY_AGENT_STATUS_CHANGE',
  
  // User Operations
  USER_CREATE = 'USER_CREATE',
  USER_UPDATE = 'USER_UPDATE',
  USER_DELETE = 'USER_DELETE',
  USER_ROLE_CHANGE = 'USER_ROLE_CHANGE',
  
  // Notification Operations
  NOTIFICATION_CREATE = 'NOTIFICATION_CREATE',
  NOTIFICATION_READ = 'NOTIFICATION_READ',
  NOTIFICATION_DELETE = 'NOTIFICATION_DELETE',
  NOTIFICATION_MARK_ALL_READ = 'NOTIFICATION_MARK_ALL_READ',
  NOTIFICATION_CLEAR_ALL = 'NOTIFICATION_CLEAR_ALL',
  
  // System Operations
  BULK_OPERATION = 'BULK_OPERATION',
  DATA_EXPORT = 'DATA_EXPORT',
  SETTINGS_UPDATE = 'SETTINGS_UPDATE',
}

export enum AuditResource {
  PRODUCT = 'PRODUCT',
  CATEGORY = 'CATEGORY',
  ORDER = 'ORDER',
  DELIVERY_AGENT = 'DELIVERY_AGENT',
  USER = 'USER',
  NOTIFICATION = 'NOTIFICATION',
  SYSTEM = 'SYSTEM',
  AUTH = 'AUTH',
}

export enum AuditSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  CRITICAL = 'CRITICAL',
}

export interface AuditMetadata {
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  browser?: string;
  os?: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet';
  location?: {
    country?: string;
    city?: string;
  };
}

export interface AuditChanges {
  before?: any;
  after?: any;
  fields?: string[]; // List of changed fields
}

export interface AuditLog {
  id: string;
  timestamp: Date;
  
  // User Information
  userId: string;
  userEmail: string;
  userName: string;
  userRole: string;
  
  // Action Information
  action: AuditAction;
  resource: AuditResource;
  resourceId: string;
  resourceName?: string;
  
  // Details
  description: string;
  changes?: AuditChanges;
  
  // Metadata
  severity: AuditSeverity;
  metadata: AuditMetadata;
  
  // Additional Context
  success: boolean;
  errorMessage?: string;
  additionalData?: Record<string, any>;
}

export interface AuditLogFilter {
  userId?: string;
  action?: AuditAction;
  resource?: AuditResource;
  startDate?: Date;
  endDate?: Date;
  severity?: AuditSeverity;
  success?: boolean;
}

export interface AuditLogStats {
  totalLogs: number;
  successfulActions: number;
  failedActions: number;
  actionsByUser: Record<string, number>;
  actionsByResource: Record<string, number>;
  recentActivity: AuditLog[];
}
